import { useEffect, useState } from "react";
import { mediaService } from "JS/Services/Media";
import { bytesToMB } from "JS/Helpers";

const extractBucketFromUrl = (urlPrefix: string): string => {
    try {
        const url = new URL(urlPrefix);
        return url.hostname.split(".")[0];
    } catch {
        return "";
    }
};

const extractPostfixAndFilename = (
    key: string,
): { postfix: string; filename: string } => {
    const parts = key.split("/").filter(Boolean);
    const filename = parts.pop() || "";
    const postfix = "/" + parts.join("/");
    return { postfix, filename };
};

/**
 * @deprecated This hook uses legacy key/bucket format. Consider using media_size from signed URL response directly.
 */
export const useFileSize = (key?: string, bucket?: string) => {
    const [size, setSize] = useState(0);
    const [loading, setLoading] = useState(false);

    async function getSize(key: string, bucket: string) {
        setLoading(true);
        const bucketName = extractBucketFromUrl(bucket);
        const { postfix, filename } = extractPostfixAndFilename(key);

        if (!bucketName || !postfix || !filename) {
            setSize(0);
            setLoading(false);
            return;
        }

        mediaService
            .getSignedUrl(bucketName, postfix, filename)
            .then((response) => {
                setSize(bytesToMB(response.media_size));
            })
            .catch(() => {
                setSize(0);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (!!key && !!bucket) getSize(key, bucket);
    }, [key, bucket]);

    return {
        size,
        loading,
    };
};

/**
 * @deprecated This hook uses legacy key/bucket format. Consider using media_size from signed URL response directly.
 */
export const useMultipleFileSizes = (
    params?: {
        key: string;
        bucket: string;
    }[],
) => {
    const [sizes, setSizes] = useState<
        {
            key: string;
            bucket: string;
            size: number;
        }[]
    >([]);

    const [loading, setLoading] = useState(false);

    async function getSizes(
        params: {
            key: string;
            bucket: string;
        }[],
    ) {
        const promises: Promise<{
            key: string;
            bucket: string;
            size: number;
        }>[] = params.map((p) => {
            const bucketName = extractBucketFromUrl(p.bucket);
            const { postfix, filename } = extractPostfixAndFilename(p.key);

            if (!bucketName || !postfix || !filename) {
                return Promise.resolve({
                    key: p.key,
                    bucket: p.bucket,
                    size: 0,
                });
            }

            return mediaService
                .getSignedUrl(bucketName, postfix, filename)
                .then((response) => ({
                    key: p.key,
                    bucket: p.bucket,
                    size: bytesToMB(response.media_size),
                }))
                .catch(() => ({
                    key: p.key,
                    bucket: p.bucket,
                    size: 0,
                }));
        });

        setLoading(true);

        return Promise.all(promises)
            .then((allSizes) => {
                setSizes(allSizes);
                return allSizes;
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (params?.length) getSizes(params);
    }, [params]);

    return {
        sizes,
        loading,
    };
};
